import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { PendingLessonUpload } from '../types/lesson';
import {
  subscribeToUpload,
  subscribeToUserUploads,
  isUploadStale,
  markStaleUploadAsFailed,
  markUploadFailed,
} from '../services/lessonUpload';

interface UsePendingUploadReturn {
  upload: PendingLessonUpload | null;
  loading: boolean;
}

/**
 * Hook to subscribe to a specific pending upload's status
 */
export function usePendingUpload(uploadId: string | null): UsePendingUploadReturn {
  const [upload, setUpload] = useState<PendingLessonUpload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uploadId) {
      setUpload(null); // eslint-disable-line react-hooks/set-state-in-effect
      setLoading(false); // eslint-disable-line react-hooks/set-state-in-effect
      return;
    }

    setLoading(true);

    const unsubscribe = subscribeToUpload(uploadId, (data) => {
      setUpload(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [uploadId]);

  return { upload, loading };
}

interface UseUserPendingUploadsReturn {
  uploads: PendingLessonUpload[];
  activeUpload: PendingLessonUpload | null;
  hasActiveUploads: boolean;
  /** True when any upload is in uploading/uploaded/processing — use to block concurrent upload CTAs. */
  hasBlockingUpload: boolean;
  loading: boolean;
}

/**
 * Hook to subscribe to all pending uploads for the current user
 * Returns active uploads (uploading, uploaded, processing, or failed)
 * Automatically marks stale uploads as failed
 */
export function useUserPendingUploads(): UseUserPendingUploadsReturn {
  const { currentUser } = useAuth();
  const [uploads, setUploads] = useState<PendingLessonUpload[]>([]);
  const [loading, setLoading] = useState(true);
  // Track which uploads we've already marked as stale to avoid repeated calls
  const markedStaleRef = useRef<Set<string>>(new Set());
  // Always-current ref so the stale-check interval doesn't close over stale state
  const uploadsRef = useRef<PendingLessonUpload[]>([]);
  uploadsRef.current = uploads;

  useEffect(() => {
    if (!currentUser) {
      setUploads([]); // eslint-disable-line react-hooks/set-state-in-effect
      setLoading(false); // eslint-disable-line react-hooks/set-state-in-effect
      return;
    }

    setLoading(true);

    const unsubscribe = subscribeToUserUploads(currentUser.uid, (data) => {
      // Check for stale uploads and mark them as failed
      data.forEach((upload) => {
        if (isUploadStale(upload) && !markedStaleRef.current.has(upload.id)) {
          markedStaleRef.current.add(upload.id);
          markStaleUploadAsFailed(upload.id).catch((err) => {
            console.error('Failed to mark stale upload as failed:', err);
          });
        }
      });

      setUploads(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  // Firestore onSnapshot only fires when the doc changes — a stuck upload at 96%
  // stops updating so the snapshot never comes. Poll every minute to catch it.
  useEffect(() => {
    const interval = setInterval(() => {
      uploadsRef.current.forEach((upload) => {
        if (isUploadStale(upload) && !markedStaleRef.current.has(upload.id)) {
          markedStaleRef.current.add(upload.id);
          const message = upload.status === 'uploading'
            ? 'Upload stalled. No progress for 5 minutes, please re-upload your video.'
            : 'Processing timed out. Please try again.';
          const code = upload.status === 'uploading' ? 'upload_failed' : 'processing_failed';
          markUploadFailed(upload.id, message, code).catch((err) => {
            console.error('Failed to mark stale upload as failed:', err);
          });
        }
      });
    }, 60_000);
    return () => clearInterval(interval);
  }, []); // runs once; reads uploadsRef which is always current

  const activeUpload = uploads.length > 0 ? uploads[0] : null;
  const hasActiveUploads = uploads.length > 0;
  const hasBlockingUpload = uploads.some(
    (u) => u.status === 'uploading' || u.status === 'uploaded' || u.status === 'processing'
  );

  return { uploads, activeUpload, hasActiveUploads, hasBlockingUpload, loading };
}

interface UploadState {
  uploadId: string | null;
  status: PendingLessonUpload['status'] | null;
  progress: number;
  error: string | null;
}

interface UseUploadManagerReturn {
  state: UploadState;
  upload: PendingLessonUpload | null;
  isActive: boolean;
  isCompleted: boolean;
  isFailed: boolean;
  startTracking: (uploadId: string) => void;
  stopTracking: () => void;
  reset: () => void;
}

/**
 * Hook to manage upload tracking state
 * Useful for the RequestLesson page to track a specific upload
 */
export function useUploadManager(): UseUploadManagerReturn {
  const [trackingUploadId, setTrackingUploadId] = useState<string | null>(null);
  const { upload } = usePendingUpload(trackingUploadId);

  const state: UploadState = {
    uploadId: trackingUploadId,
    status: upload?.status ?? null,
    progress: upload?.uploadProgress ?? 0,
    error: upload?.errorMessage ?? null,
  };

  const startTracking = useCallback((uploadId: string) => {
    setTrackingUploadId(uploadId);
  }, []);

  const stopTracking = useCallback(() => {
    setTrackingUploadId(null);
  }, []);

  const reset = useCallback(() => {
    setTrackingUploadId(null);
  }, []);

  const isActive = upload !== null &&
    ['uploading', 'uploaded', 'processing'].includes(upload.status);
  const isCompleted = upload?.status === 'completed';
  const isFailed = upload?.status === 'failed';

  return {
    state,
    upload,
    isActive,
    isCompleted,
    isFailed,
    startTracking,
    stopTracking,
    reset,
  };
}
